import React, { useState, useEffect } from 'react';
import Layout from 'Component/Layouts/layout';
import { Box, Button, Typography, Card, CardContent, Chip } from '@mui/material';
import { Assignment, Add, Visibility, Download } from '@mui/icons-material';
import TableComponent from 'Component/Table';
import useApiCaller from 'hooks/apiCaller';
import { useNavigate } from 'react-router-dom';
import FormModal from 'Component/FormModal';
import CreateDeclarationForm from 'Component/MultiStepTrainingForms/TrainingForm/CreateDeclarationForm';
import UploadDeclarationForm from 'Component/MultiStepTrainingForms/TrainingForm/UploadDeclarationForm';
import AssignRemoveDeclarationForm from 'Component/MultiStepTrainingForms/TrainingForm/AssignRemoveDeclarationForm';
import dayjs from 'dayjs'; // Import dayjs for date formatting
import useCredentials from 'hooks/useCredentials';

const Declaration = () => {
  const [allCourses, setAllCourses] = useState([]);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const apiCaller = useApiCaller();
  const { userData: user, logout } = useCredentials();

  const handleDownloadFile = (url) => {
    if (!url) {
      alert('File not available');
      return;
    }
    window.open(url, '_blank'); // Open file in new tab or trigger download
  };
  console.log('user', user);
  const headerBtnSx = {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.813rem',
    borderRadius: '8px',
    px: 2,
    py: 0.875,
    boxShadow: 'none',
    '&:hover': { boxShadow: 'none' },
  };

  const isNew = (row) => {
    if (!row.createdAt) return false;
    const diffDays = (Date.now() - new Date(row.createdAt).getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= 30;
  };

  const columns = [
    // { label: "DeclarationID", field: "courseId" },
    {
      label: 'Declaration Name',
      field: 'courseName',
      render: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          {row.courseName}
          {isNew(row) && (
            <Chip
              label="NEW"
              size="small"
              sx={{
                backgroundColor: '#22c55e',
                color: '#fff',
                fontWeight: 700,
                fontSize: '10px',
                height: '18px',
              }}
            />
          )}
        </Box>
      ),
    },
    {
      label: 'Deadline',
      field: 'endDate',
      render: (row) => (row.endDate ? dayjs(row.endDate).format('DD-MM-YYYY') : 'N/A'),
    },
    {
      label: 'Download Template',
      field: 'signedURL',
      render: (row) =>
        row.signedURL ? (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleDownloadFile(row.signedURL)}
          >
            Download
          </Button>
        ) : (
          'Not Available'
        ),
    },
    {
      label: 'Action Required',
      field: 'actionRequired',
      render: (row) => {
        const isYes = String(row.actionRequired).toLowerCase() === 'yes';
        return isYes ? (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              px: 1.2,
              py: 0.4,
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 700,
              color: '#fff',
              backgroundColor: '#dc2626',
              animation: 'arBlink 1.2s ease-in-out infinite',
              '@keyframes arBlink': {
                '0%, 100%': { opacity: 1, boxShadow: '0 0 6px 2px rgba(220,38,38,0.6)' },
                '50%': { opacity: 0.55, boxShadow: 'none' },
              },
            }}
          >
            Yes
          </Box>
        ) : (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              px: 1.2,
              py: 0.4,
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#15803d',
              backgroundColor: '#dcfce7',
            }}
          >
            No
          </Box>
        );
      },
    },
    {
      label: 'Action',
      field: 'edit',
      render: (row) => {
        // Feature 3: Auto-disable upload once user has submitted (actionRequired = 'No')
        const isSubmitted = String(row.actionRequired).toLowerCase() === 'no';
        if (isSubmitted) {
          return (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                px: 1.4,
                py: 0.5,
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#15803d',
                backgroundColor: '#dcfce7',
                border: '1px solid #bbf7d0',
              }}
            >
              Submitted
            </Box>
          );
        }
        return (
          <FormModal
            buttonName={'Upload'}
            title={'Upload Dec Document'}
            form={<UploadDeclarationForm row={row} user={user} />}
          />
        );
      },
    },
  ];

  const handleShowCourses = async () => {
    try {
      const response = await apiCaller.post('/super-user/fetch-all-declaration');
      if (response) {
        const sorted = (response?.data?.data?.result || []).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setAllCourses(sorted);
        setCount(sorted.length);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    handleShowCourses();
  }, []);

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Professional Header */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                sx={{
                  color: '#1e293b',
                  mb: 0.25,
                }}
              >
                Declaration Section
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
                Manage employee declarations and compliance documents
              </Typography>
            </Box>
            {user && user.roles.includes('admin') && (
              <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                <FormModal
                  buttonName={'Assign/Remove'}
                  title={'Assign/remove Declaration'}
                  form={<AssignRemoveDeclarationForm />}
                  buttonSx={headerBtnSx}
                />
                <FormModal
                  buttonName={'Create Declaration'}
                  title={'Create Declaration'}
                  form={<CreateDeclarationForm />}
                  buttonSx={headerBtnSx}
                />
                <Button
                  variant="contained"
                  startIcon={<Visibility />}
                  onClick={() => navigate('/view-emp-declaration')}
                  sx={headerBtnSx}
                >
                  View Submissions
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* Table Section */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <TableComponent
              tableData={allCourses}
              setTableData={setAllCourses}
              columns={columns}
              tableName={'Course'}
              count={count}
              useWholeRowClicked={false}
              getRowStyle={(row) =>
                isNew(row) ? { backgroundColor: '#f0fdf4', borderLeft: '4px solid #22c55e' } : {}
              }
            />
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default Declaration;
